import { useCallback, useMemo, useState } from "react";
import { useJobRequests } from "./useJobRequests";
import { IJob, Job, JobStatus, useJobs } from "./useJobs";
import { useSummaries } from "./useSummaries";

const PAGE_SIZE = 25;

export const useEnrichedJobs = () => {
  const [limitCount, setLimitCount] = useState(PAGE_SIZE);
  const {
    jobRequests,
    isLoading: isLoadingRequests,
    error: errorRequests,
  } = useJobRequests(limitCount);
  const {
    jobs,
    isLoading: isLoadingJobs,
    error: errorJobs,
  } = useJobs(limitCount);
  const {
    metadatas,
    isLoading: isLoadingSummaries,
    error: errorSummaries,
  } = useSummaries(limitCount);

  const loadMore = useCallback(() => {
    setLimitCount((count) => count + PAGE_SIZE);
  }, []);

  const enrichedJobs = useMemo<IJob[]>(() => {
    const allJobsMap = new Map<string, IJob>();

    jobs.forEach((job: IJob) => {
      allJobsMap.set(job.jobId, job);
    });

    // Add job requests only if a job with the same ID doesn't already exist.
    jobRequests.forEach((jobRequest: IJob) => {
      if (!allJobsMap.has(jobRequest.jobId)) {
        allJobsMap.set(jobRequest.jobId, jobRequest);
      }
    });

    // Create a lookup map for summary metadata by jobId
    const metadataByJobId = new Map();
    metadatas.forEach((metadata) => {
      metadataByJobId.set(metadata.jobId, metadata);
    });

    // Enrich jobs with summary metadata
    const enrichedJobs = Array.from(allJobsMap.values()).map((job) => {
      const metadata = metadataByJobId.get(job.jobId);

      // Spread job and materialize getters explicitly
      return {
        ...job,
        fileName: job.fileName, // Materialize getter
        ...(job instanceof Job && { summaryUri: job.summaryUri }), // Materialize getter (only exists on Job class)
        manuscriptTitle: metadata?.manuscriptTitle,
        manuscriptAuthor: metadata?.manuscriptAuthor,
      };
    });

    // Filter out deleted jobs and sort by date
    return enrichedJobs
      .filter((job) => job.status !== JobStatus.DELETED)
      .sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
  }, [jobRequests, jobs, metadatas]);

  const isLoading = isLoadingRequests || isLoadingJobs || isLoadingSummaries;
  const error = errorRequests || errorJobs || errorSummaries;
  const hasMore = jobs.length >= limitCount || jobRequests.length >= limitCount;

  return { jobs: enrichedJobs, hasMore, loadMore, isLoading, error };
};
